import type { DashboardPoint } from '@/api/generated';

export interface WeekBucket {
  monday: string;
  retailMinor: number;
}

function parseCivil(civil: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(civil);
  if (match === null) return null;
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

function toCivil(year: number, month: number, day: number): string {
  const y = String(year).padStart(4, '0');
  const m = String(month).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Monday of the ISO week that contains the civil date (local calendar, never UTC-parsed). */
function mondayOf(year: number, month: number, day: number): string {
  const date = new Date(year, month - 1, day);
  const weekday = date.getDay();
  const offset = weekday === 0 ? -6 : 1 - weekday;
  const monday = new Date(year, month - 1, day + offset);
  return toCivil(monday.getFullYear(), monday.getMonth() + 1, monday.getDate());
}

/** Bucket `series[]` points by ISO week (Monday start). Unsorted input is sorted by Monday. */
export function bucketByWeek(points: DashboardPoint[]): WeekBucket[] {
  const totals = new Map<string, number>();
  for (const point of points) {
    const parts = parseCivil(point.date);
    if (parts === null) continue;
    const monday = mondayOf(parts.year, parts.month, parts.day);
    totals.set(monday, (totals.get(monday) ?? 0) + point.retailMinor);
  }
  return [...totals.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([monday, retailMinor]) => ({ monday, retailMinor }));
}
